import { TabPaneProps, Tabs } from "antd";
import { useDispatch } from "app/store";
import ListPageLayout from "components/layout/ListPageLayout";
import TabsWrapper from "components/ui/TabsWrapper";
import Claims from "features/auth/claims";
import useAuth from "features/auth/useAuth";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import UsersAndRolesAssign from "./usersAndRolesAssign/UsersAndRolesAssign";
import PermissionsAndRolesAssign from "./permissionsAndRolesAssign/PermissionsAndRolesAssign";
import UsersAndPermissionsAssign from "./usersAndPermissionsAssign/UsersAndPermissionsAssign";
import UsersList from "./usersList/UsersList";

import { useSearchParams } from "react-router-dom";
import RolesList from "./rolesList/RolesList";

interface CustomTabPaneProps extends Omit<TabPaneProps, "key"> {
  key: string;
}

const RolesAndPermissionsSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasClaim } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams("");
  const activeTabParam = searchParams.get("tab");

  const tabs = useMemo<CustomTabPaneProps[]>(
    () => [
      ...(hasClaim([Claims.User.Read])
        ? ([
            {
              tab: t("settings.tabs.users"),
              key: "users",
              children: <UsersList />,
            },
          ] as CustomTabPaneProps[])
        : []),
      ...(hasClaim([Claims.Role.Read])
        ? ([
            {
              tab: t("settings.tabs.roles"),
              key: "roles",
              children: <RolesList />,
            },
          ] as CustomTabPaneProps[])
        : []),
      ...(hasClaim([Claims.Option.Read])
        ? ([
            {
              tab: t("rolesAndPermissionsSettings.tabs.usersAndRolesAssign"),
              key: "usersAndRolesAssign",
              children: <UsersAndRolesAssign />,
            },
            {
              tab: t(
                "rolesAndPermissionsSettings.tabs.permissionsAndRolesAssign"
              ),
              key: "permissionsAndRolesAssign",
              children: <PermissionsAndRolesAssign />,
            },
            {
              tab: t(
                "rolesAndPermissionsSettings.tabs.usersAndPermissionsAssign"
              ),
              key: "usersAndPermissionsAssign",
              children: <UsersAndPermissionsAssign />,
            },
          ] as CustomTabPaneProps[])
        : []),
    ],
    [hasClaim, t]
  );

  const defaultActiveKey = useMemo(() => {
    const activeTab = tabs.find((tab) => tab.key === activeTabParam);

    return activeTab ? activeTab.key : tabs[0].key;
  }, [activeTabParam, tabs]);

  const handleTabsChange = useCallback(
    (activeKey: string) => {
      setSearchParams(new URLSearchParams(`tab=${activeKey}`));
    },
    [setSearchParams]
  );

  useEffect(() => {
    return () => {
      dispatch.usersAndRolesAssign.reset();
      dispatch.permissionsAndRolesAssign.reset();
      dispatch.usersAndPermissionsAssign.reset();
    };
  }, [
    dispatch.permissionsAndRolesAssign,
    dispatch.usersAndPermissionsAssign,
    dispatch.usersAndRolesAssign,
  ]);

  return (
    <ListPageLayout title={t("rolesAndPermissionsSettings.title")}>
      <TabsWrapper>
        <Tabs
          defaultActiveKey={defaultActiveKey}
          destroyInactiveTabPane
          onChange={handleTabsChange}
          type="card"
          size="small"
        >
          {tabs.map(({ tab, key, children }) => (
            <Tabs.TabPane tab={tab} key={key}>
              {children}
            </Tabs.TabPane>
          ))}
        </Tabs>
      </TabsWrapper>
    </ListPageLayout>
  );
};

export default RolesAndPermissionsSettings;
